// The assistant's side of POST /api/assistant/messages (apps/server/src/assistant.mjs)
// and of the person's saved conversations (assistantConversations.mjs): what the panel
// sends and what it makes of the answers. The server keeps each person's conversation;
// once it has given one an id, the panel sends only the new question with that id. Where
// the server saves nothing (it answers conversation_id: null), the panel carries the
// earlier answered exchanges itself, as before.
import type { CurrentUser } from '../auth/api';
import { type ProposalView, proposalsFrom } from './proposals';
import type { Viewing } from './viewing';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** One message as the route takes it. */
export interface Message {
  role: 'user' | 'assistant';
  content: string;
}

/** One question and what became of it: answered, still waiting, or failed. */
export interface Exchange {
  id: number;
  question: string;
  reply?: string;
  /** False when the server has no model, so the reply is its fixed "not connected" text. */
  connected?: boolean;
  error?: string;
  /** Changes the reply proposes (MOTHER-49), each waiting for the person to approve or reject. Not saved with the conversation. */
  proposals?: ProposalView[];
}

/** The route's limits (assistant.mjs). */
export const MAX_MESSAGES = 40;
export const MAX_MESSAGE_LENGTH = 4000;

/**
 * The roles that may use the assistant: those with `use_everything` (apps/server/src/access.mjs
 * ROLES). The route refuses everyone else, so the launcher is not shown to them.
 */
const ASSISTANT_ROLES = ['admin'];

export function canUseAssistant(user: Pick<CurrentUser, 'role'>): boolean {
  return user.role !== undefined && ASSISTANT_ROLES.includes(user.role);
}

/**
 * What to send for a new question: the earlier exchanges that got a reply, oldest
 * first, then the question; failed and waiting ones are left out. Kept within the
 * route's limits: the most recent messages only, starting with a question, and a
 * long earlier reply cut to the length a message may have.
 */
export function messagesFor(exchanges: readonly Exchange[], question: string): Message[] {
  const earlier = exchanges.flatMap((e): Message[] =>
    e.reply === undefined
      ? []
      : [
          { role: 'user', content: e.question },
          { role: 'assistant', content: e.reply.slice(0, MAX_MESSAGE_LENGTH) },
        ],
  );
  // Whole exchanges only (an even count), so with the question there are at most
  // MAX_MESSAGES - 1 and the first kept is a question, as the last is.
  return [...(earlier.length > MAX_MESSAGES - 2 ? earlier.slice(-(MAX_MESSAGES - 2)) : earlier), { role: 'user', content: question }];
}

/** The body POST /api/assistant/messages takes. */
export interface AssistantRequest {
  conversation_id?: string;
  messages: Message[];
  viewing?: { object: string; id: string };
}

/**
 * The body for a new question: with a saved conversation, its id and the question
 * alone (the server holds the rest); without one, `messagesFor`. Asked from a record
 * page, `viewing` names that record (MOTHER-186); a saved conversation carries no
 * page, so each question says the page it was asked from, or nothing.
 */
export function requestFor(
  exchanges: readonly Exchange[],
  question: string,
  conversationId: string | null,
  viewing: Viewing | null = null,
): AssistantRequest {
  const hint = viewing === null ? {} : { viewing: { object: viewing.object, id: viewing.id } };
  if (conversationId !== null) return { conversation_id: conversationId, messages: [{ role: 'user', content: question }], ...hint };
  return { messages: messagesFor(exchanges, question), ...hint };
}

/** A saved conversation's messages as exchanges: each question with the reply after it. */
export function exchangesFrom(messages: readonly Message[]): Exchange[] {
  const exchanges: Exchange[] = [];
  for (const message of messages) {
    if (message.role === 'user') exchanges.push({ id: exchanges.length, question: message.content });
    else {
      const last = exchanges.at(-1);
      // A reply with no question before it (the oldest kept message) is left out.
      if (last !== undefined && last.reply === undefined) {
        last.reply = message.content;
        last.connected = true;
      }
    }
  }
  // A question saved without its reply has nothing to wait for.
  return exchanges.filter((e) => e.reply !== undefined);
}

async function payloadOf(response: Response): Promise<Record<string, unknown>> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  return typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : {};
}

const idOf = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);

/**
 * Sends a question (`requestFor`); resolves to the reply and the conversation it was
 * saved in (null: saved nowhere), or rejects with the server's message.
 */
export async function askAssistant(
  fetcher: Fetch,
  body: AssistantRequest,
): Promise<{ reply: string; connected: boolean; conversationId: string | null; proposals: ProposalView[] }> {
  const response = await fetcher('/api/assistant/messages', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const record = await payloadOf(response);
  if (response.ok && typeof record.reply === 'string') {
    return {
      reply: record.reply,
      connected: record.connected !== false,
      conversationId: idOf(record.conversation_id),
      proposals: proposalsFrom(record.proposals).map((proposal) => ({ proposal, state: 'pending' })),
    };
  }
  if (response.status === 404) throw new Error('That conversation is gone. Start a new one.');
  throw new Error(typeof record.error === 'string' && record.error !== '' ? record.error : 'The assistant could not answer. Try again.');
}

/** The server's message in an error answer, else `fallback`. */
const errorOf = (record: Record<string, unknown>, fallback: string): string =>
  typeof record.error === 'string' && record.error !== '' ? record.error : fallback;

const conversationUrl = (conversationId: string): string => `/api/assistant/conversations/${encodeURIComponent(conversationId)}`;

/** A saved conversation's answer ({ conversation_id, messages }) as the panel shows it. */
function savedConversation(record: Record<string, unknown>): { conversationId: string | null; exchanges: Exchange[] } {
  const messages = Array.isArray(record.messages)
    ? (record.messages as unknown[]).filter(
        (m): m is Message =>
          typeof m === 'object' && m !== null && ((m as Message).role === 'user' || (m as Message).role === 'assistant') && typeof (m as Message).content === 'string',
      )
    : [];
  return { conversationId: idOf(record.conversation_id), exchanges: exchangesFrom(messages) };
}

/** The person's most recent saved conversation, or none. */
export async function loadLatest(fetcher: Fetch): Promise<{ conversationId: string | null; exchanges: Exchange[] }> {
  const response = await fetcher('/api/assistant/conversations/latest');
  const record = await payloadOf(response);
  if (!response.ok) throw new Error('Your last conversation could not be loaded.');
  return savedConversation(record);
}

/** One past conversation as the history lists it (Foundation's ConversationSwitcher). */
export interface ConversationSummary {
  id: string;
  title: string;
}

/** A conversation title's limit, in code points (assistantConversations.mjs MAX_TITLE). */
export const MAX_TITLE_LENGTH = 120;

/**
 * The person's saved conversations, newest first, as GET /api/assistant/conversations
 * answers them (MOTHER-148); rejects with the server's message.
 */
export async function listConversations(fetcher: Fetch): Promise<ConversationSummary[]> {
  const response = await fetcher('/api/assistant/conversations');
  const record = await payloadOf(response);
  if (!response.ok || !Array.isArray(record.conversations)) throw new Error(errorOf(record, 'Your conversations could not be loaded. Try again.'));
  return (record.conversations as unknown[]).flatMap((c): ConversationSummary[] => {
    if (typeof c !== 'object' || c === null) return [];
    const { id, title } = c as Record<string, unknown>;
    return typeof id === 'string' && id !== '' && typeof title === 'string' ? [{ id, title }] : [];
  });
}

/** One of the person's saved conversations, to carry on in; rejects with the server's message (404: it is gone). */
export async function loadConversation(fetcher: Fetch, conversationId: string): Promise<{ conversationId: string; exchanges: Exchange[] }> {
  const response = await fetcher(conversationUrl(conversationId));
  const record = await payloadOf(response);
  if (!response.ok) {
    throw new Error(response.status === 404 ? 'That conversation is gone.' : errorOf(record, 'That conversation could not be loaded. Try again.'));
  }
  const saved = savedConversation(record);
  return { conversationId: saved.conversationId ?? conversationId, exchanges: saved.exchanges };
}

/** Gives one of the person's conversations `title`; resolves to the title saved, or rejects with the server's message. */
export async function renameConversation(fetcher: Fetch, conversationId: string, title: string): Promise<string> {
  const response = await fetcher(conversationUrl(conversationId), {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  const record = await payloadOf(response);
  if (!response.ok) throw new Error(errorOf(record, 'The conversation could not be renamed. Try again.'));
  return typeof record.title === 'string' ? record.title : title;
}

/** Deletes one of the person's conversations; one already gone counts as deleted. */
export async function deleteConversation(fetcher: Fetch, conversationId: string): Promise<void> {
  const response = await fetcher(conversationUrl(conversationId), { method: 'DELETE' });
  if (response.ok || response.status === 404) return;
  throw new Error('The conversation could not be deleted. Try again.');
}
